import {Injectable, EventEmitter} from '@angular/core';
import {Questionnaire} from '@ngmedax/common-questionnaire-types';
import * as sha from 'js-sha256';

import {QuestionnaireVariablesService} from './questionnaire-variables.service';
import {QuestionnaireFormatService} from './questionnaire-format.service';
import {ElementPositionChange, ElementChange} from '../../types';
import {SCHEMA_VERSION} from '../../../schema-version';


// scopes for which mapped variables (e.g. scoring/gdt) get copied along when duplicating a container
const COPY_VARIABLE_SCOPES = ['scoring', 'gdt'];


@Injectable()
export class QuestionnaireStateService {
  private questionnaire: Questionnaire;
  private pathHashMap: any;
  public onAddElement: EventEmitter<ElementChange> = new EventEmitter();
  public onUpdateElement: EventEmitter<ElementChange> = new EventEmitter();
  public onDeleteElement: EventEmitter<ElementChange> = new EventEmitter();
  public onElementPositionChange: EventEmitter<ElementPositionChange> = new EventEmitter();
  public onSetQuestionnaire: EventEmitter<Questionnaire> = new EventEmitter();
  public onMoveToPage: EventEmitter<number> = new EventEmitter();
  public onOpenScoringVariables: EventEmitter<void> = new EventEmitter();

  /**
   * Injects dependencies
   */
  public constructor(
    private questionnaireVariables: QuestionnaireVariablesService,
    private questionnaireFormat: QuestionnaireFormatService
  ) {
  }

  /**
   * Sets the questionnaire
   *
   * @param {Questionnaire} questionnaire
   */
  public setQuestionnaire(questionnaire: Questionnaire) {
    this.questionnaire = questionnaire;

    if (this.questionnaire.questions) {
      for (const question of this.questionnaire.questions) {
        this.addPathHashes(question);
      }
    }

    this.questionnaireVariables.setQuestionnaire(this.questionnaire);
    this.onSetQuestionnaire.emit(questionnaire);
  }

  /**
   * Returns the questionnaire
   *
   * @returns {Questionnaire}
   */
  public getQuestionnaire() {
    // always update schema version
    this.questionnaire.softwareVersion = SCHEMA_VERSION;
    return this.questionnaire;
  }

  /**
   * Sets the questionnaire path hash map
   *
   * @param {any} pathHashes
   */
  public setPathHashMap(pathHashMap: any) {
    this.pathHashMap = pathHashMap;
  }

  /**
   * Returns the questionnaire path hash map
   *
   * @returns {any}
   */
  public getPathHashMap() {
    return this.pathHashMap;
  }

  /**
   * Deletes a path hash from the hash map
   *
   * @param {string} pathHash
   */
  public deletePathHash(pathHash: string) {
    if (typeof this.pathHashMap[pathHash] !== 'undefined') {
      delete(this.pathHashMap[pathHash]);
    }
  }

  /**
   * Adds an element to the questionnaire
   *
   * @param {Questionnaire.Container} element
   * @param {number} position
   */
  public addElement(element: Questionnaire.Container, position: number = null) {
    // if position found
    if (position !== null) {
      // reorder position. adds the element somewhere between
      this.questionnaire.questions.splice((position + 1) , 0, element);
    } else {
      // add new element to questionnaire
      this.questionnaire.questions.push(element);
    }

    // update path hashes in path hash map
    this.addPathHashes(element);

    // emit "add" event
    this.onAddElement.emit({
      position,
      element,
    });
  }

  /**
   * Returns element by its path hash
   *
   * @param pathHash
   */
  public getElement(pathHash: string) {
    return this.pathHashMap[pathHash];
  }

  /**
   * Updates an element of the questionnaire. This will only trigger the "element change" event.
   *
   * @param {Questionnaire.Container} element
   * @param {number} position
   */
  public updateElement(element: Questionnaire.Container, position: number) {
    // update path hashes in path hash map
    this.addPathHashes(element);

    // emit "change" event
    this.onUpdateElement.emit({
      element,
      position
    });
  }

  /**
   * Deletes an element from the questionnaire
   *
   * @param {number} position
   */
  public deleteElement(position: number) {
    // early bailout if no element on position
    if (typeof this.questionnaire.questions[position] === 'undefined') {
      return;
    }

    // get element by position
    const element = this.questionnaire.questions[position];

    // delete all variable mappings of container element
    this.questionnaireVariables.deleteVariableMappings(element);

    // remove path hashes of this element from path hash map
    this.removePathHashes(element);

    // remove element from questionnaire
    this.questionnaire.questions.splice(position, 1);

    // emit "delete" event
    this.onDeleteElement.emit({
      position,
      element
    });
  }

  /**
   * Changes the position of an element from given current position to new position
   *
   * @param {number} currentPosition
   * @param {number} newPosition
   */
  public changeElementPosition(currentPosition: number, newPosition: number) {
    // get questionnaire questions
    const questions = this.questionnaire.questions;

    // early bailout when changing positions does not make sense
    if (currentPosition > (questions.length) || newPosition < 0) {
      return;
    }

    if (currentPosition !== newPosition) {
      // reorder element by current and new position
      questions.splice(newPosition, 0, questions.splice(currentPosition, 1)[0]);
    }

    // emit "position change" event
    this.onElementPositionChange.emit({
      currentPosition,
      newPosition
    });
  }

  /**
   * Returns true if given position of an element is the last one
   *
   * @param position
   */
  public isOnLastPosition(position: number): boolean {
    return (position + 1) === this.questionnaire.questions.length;
  }

  /**
   * Recursively creates and adds path hash to element. Also recursively adds element to path hash map
   *
   * @param {Questionnaire.Container} element
   */
  public addPathHashes(element: Questionnaire.Container, path: string = null) {
    // build new path
    path = path ? `${path}.${element.id}` : element.id;

    // build and add path hash
    element.pathHash = sha.sha256(path);

    // for debugging only
    element.path = path;

    // add element to path hash map
    this.pathHashMap[element.pathHash] = element;

    if (element.elements) {
      for (const subElement of element.elements) {
        // elements with "omitContainer: true" don't get a path hash. in this case we use the parent hash!
        subElement.parentHash = element.pathHash || element.parentHash;
        this.addPathHashes(subElement, path);

        // yes/no/misc json default structure contains condition.show, which contains an uuid of an element
        if (subElement.conditions && subElement.conditions.show && typeof subElement.conditions.show === 'string') {
          // we will search for the element and map it by its path hash
          for (const matchingElement of element.elements) {
            if (matchingElement.id === subElement.conditions.show) {
              // maybe the matching element does not yet contain a path hash, so lets add it
              this.addPathHashes(matchingElement, path);
              // map sub element. fill out app will show element if matching element was answered
              subElement.conditions.show = [matchingElement.pathHash];
            }
          }
        }
      }
    }
  }

  /**
   * Searches for path hashes in given element and removes them from the given hash map
   *
   * @param {Questionnaire.Container} element
   */
  private removePathHashes(element: Questionnaire.Container) {
    if (element.pathHash && typeof this.pathHashMap[element.pathHash] !== 'undefined') {
      delete(this.pathHashMap[element.pathHash]);
    }

    if (element.elements) {
      for (const subElement of element.elements) {
        this.removePathHashes(subElement);
      }
    }
  }

  /**
   * Creates a deep copy of an element that is ready to be inserted as a sibling
   * of the source. Generates a fresh top-level uuid, recomputes path hashes and
   * remaps internal references (mapped variables and conditions) so they point
   * to the new copy instead of the source. References pointing to elements
   * outside the copy are left untouched.
   *
   * Child element ids are intentionally preserved (template-defined ids like
   * the yes/no/sonstiges options stay the same per design) - uniqueness is
   * achieved through the new top-level uuid which feeds into all child path
   * hashes.
   *
   * @param {Questionnaire.Container} source The element to duplicate
   * @returns {Questionnaire.Container} The duplicated element
   */
  public duplicateElement(source: Questionnaire.Container): Questionnaire.Container {
    const clone: Questionnaire.Container = JSON.parse(JSON.stringify(source));

    clone.id = this.questionnaireFormat.generateUUID();

    this.preserveOldPathHashes(clone);

    // recompute path hashes for the clone using the new top-level uuid
    this.addPathHashes(clone);

    for (const scope of COPY_VARIABLE_SCOPES) {
      this.copyVariableMappings(clone, scope);
    }

    this.createDuplicateUploadVariables(clone);
    this.remapConditionReferences(clone);
    this.removeOldPathHashes(clone);

    return clone;
  }

  /** Gives each copied upload-enabled container its own valid filename prefix. */
  private createDuplicateUploadVariables(container: Questionnaire.Container): void {
    if (container.options && container.options.allowUpload) {
      this.questionnaireVariables.ensureUploadVariable(container);
    }
    if (container.elements) {
      container.elements.forEach(child => this.createDuplicateUploadVariables(child));
    }
  }

  /**
   * Stores the current pathHash of every element as oldPathHash so we can
   * later build an old -> new path hash map after re-hashing.
   *
   * @param {Questionnaire.Container} element
   */
  private preserveOldPathHashes(element: Questionnaire.Container) {
    const walk = (container: Questionnaire.Container) => {
      container.pathHash && (container['oldPathHash'] = container.pathHash);
      container.elements && container.elements.forEach(child => walk(child));
    };

    walk(element);
  }

  /**
   * Removes the oldPathHash marker property from every element after duplication
   * has finished.
   *
   * @param {Questionnaire.Container} element
   */
  private removeOldPathHashes(element: Questionnaire.Container) {
    const walk = (container: Questionnaire.Container) => {
      container['oldPathHash'] && delete(container['oldPathHash']);
      container.elements && container.elements.forEach(child => walk(child));
    };

    walk(element);
  }

  /**
   * Copies variable mappings (e.g. scoring/gdt) from the source container to
   * the new copy. Reads mappings of the source via oldPathHash and registers
   * them again under the new pathHash.
   *
   * @param {Questionnaire.Container} container
   * @param {string} scope The variable scope (e.g. "scoring", "gdt")
   */
  private copyVariableMappings(container: Questionnaire.Container, scope: string) {
    const walk = (current: Questionnaire.Container) => {
      if (current['oldPathHash']) {
        const mappedVarName = this.questionnaireVariables.getMappedVariableName(scope, current['oldPathHash']);

        if (mappedVarName) {
          const opts = this.questionnaireVariables.getMappedVariableOptions(scope, current['oldPathHash'], mappedVarName);
          opts && this.questionnaireVariables.addVariableMapping(scope, mappedVarName, current.pathHash, opts);
        }
      }

      current.elements && current.elements.forEach(child => walk(child));
    };

    walk(container);
  }

  /**
   * Remaps condition references inside a duplicated container. After copying,
   * conditions.show (array of path hashes) and conditions.expression (string
   * containing container.x{hash} tokens) still reference the source's elements.
   * This rewrites them so they point to the path hashes of the new copy.
   * Hashes pointing to elements outside the copy are left untouched.
   *
   * Must be called while oldPathHash is still preserved on the elements and
   * after path hashes have been recomputed.
   *
   * @param {Questionnaire.Container} container
   */
  private remapConditionReferences(container: Questionnaire.Container) {
    const oldToNew: {[oldHash: string]: string} = {};

    const collect = (current: Questionnaire.Container) => {
      if (current['oldPathHash'] && current.pathHash) {
        oldToNew[current['oldPathHash']] = current.pathHash;
      }
      current.elements && current.elements.forEach(child => collect(child));
    };

    collect(container);

    if (Object.keys(oldToNew).length === 0) {
      return;
    }

    const remap = (current: Questionnaire.Container) => {
      const conditions: any = current.conditions;

      if (conditions) {
        // remap conditions.show entries (array form). string form (fresh template)
        // gets resolved by addPathHashes later, so we leave it alone.
        if (Array.isArray(conditions.show)) {
          conditions.show = conditions.show.map((hash: string) => oldToNew[hash] || hash);
        }

        // remap conditions.expression: rewrite container.x{oldHash} -> container.x{newHash}.
        // hashes pointing to elements outside the copy are not in the map and stay untouched.
        if (typeof conditions.expression === 'string') {
          conditions.expression = conditions.expression.replace(
            /container\.x([a-f0-9]{64})/g,
            (match: string, oldHash: string) => oldToNew[oldHash] ? 'container.x' + oldToNew[oldHash] : match
          );
        }
      }

      current.elements && current.elements.forEach(child => remap(child));
    };

    remap(container);
  }
}
